/**
 * Rule-based Growth System Snapshot scoring.
 * No AI, no fake ROI numbers — pure deterministic logic on the user's answers.
 */

export const QUESTIONS = [
    {
        id: 'business_type',
        type: 'single',
        required: true,
        label: 'What type of business are you building this for?',
        options: [
            'Dental clinic',
            'Aesthetic / wellness clinic',
            'Consultant / expert',
            'Course / event provider',
            'E-commerce brand',
            'Local service business',
            'Other',
        ],
    },
    {
        id: 'current_setup',
        type: 'multi',
        required: false,
        label: 'What do you currently have?',
        help: 'Select all that apply.',
        options: [
            'No website yet',
            'Basic website',
            'Website with booking/contact form',
            'Landing pages for campaigns',
            'Ads running',
            'Dashboard / CRM',
            'AI or automation tools',
            'Not sure',
        ],
    },
    {
        id: 'main_goal',
        type: 'single',
        required: true,
        label: 'What do you want this system to achieve first?',
        micro: 'Small gaps here often lead to lost clients.',
        options: [
            'More enquiries',
            'More booked consultations',
            'Better lead quality',
            'Faster follow-up',
            'Better lead tracking',
            'More online sales',
            'Launch a new offer',
            'Improve internal operations',
        ],
    },
    {
        id: 'biggest_leak',
        type: 'single',
        required: true,
        label: 'Where do you think the biggest leak is right now?',
        micro: 'This is where most businesses lose enquiries.',
        options: [
            'People visit but do not enquire',
            'Leads enquire but do not book',
            'Leads are low quality',
            'Follow-up is slow or inconsistent',
            'We do not know where leads come from',
            'The website does not explain the offer well',
            'The business looks less premium than it is',
            'Operations are messy behind the scenes',
            'Not sure',
        ],
    },
    {
        id: 'lead_sources',
        type: 'multi',
        required: false,
        label: 'How do people usually find you now?',
        help: 'Select all that apply.',
        options: [
            'Referrals / word of mouth',
            'Instagram / Facebook organic',
            'Paid ads',
            'Google search',
            'Google Maps',
            'Events / offline',
            'Existing database',
            'Not consistent yet',
        ],
    },
    {
        id: 'after_enquiry',
        type: 'single',
        required: false,
        label: 'What happens after a lead contacts you?',
        micro: 'This step usually determines whether someone books or leaves.',
        options: [
            'We reply manually when we can',
            'Reception/team calls them',
            'They book directly online',
            'They go into a CRM',
            'They receive automated follow-up',
            'We track status and outcome',
            'Honestly, it is messy',
        ],
    },
    {
        id: 'timeline',
        type: 'single',
        required: true,
        label: 'When do you want this improved?',
        options: [
            'Immediately',
            'This month',
            'In the next 1–3 months',
            'Just exploring',
        ],
    },
    {
        id: 'budget',
        type: 'single',
        required: true,
        label: 'What level of investment are you considering?',
        options: [
            'Under €500',
            '€500–€1,000',
            '€1,000–€2,500',
            '€2,500–€5,000',
            '€5,000+',
        ],
    },
];

export const SYSTEMS = {
    website: 'Conversion Website',
    funnel: 'Campaign Funnel',
    dashboard: 'Lead Tracking Dashboard',
    ai: 'AI Receptionist / Follow-Up Workflow',
    custom: 'Custom Web App / Internal System',
    ecom: 'E-Commerce Sales Flow',
};

const DEFAULT_SECONDARY = {
    website: 'funnel',
    funnel: 'dashboard',
    dashboard: 'ai',
    ai: 'dashboard',
    custom: 'dashboard',
    ecom: 'funnel',
};

const RESULT_COPY = {
    website: {
        explanation:
            'Visitors are landing on your site — but not staying long enough to understand why they should trust you, what you offer, and what to do next. This is where your system is leaking.',
        bottleneck:
            'You\'re likely losing clients at this stage: visitors arrive, don\'t get clarity fast enough, and leave before enquiring.',
        next_steps: [
            'Clarify the main offer or service you want visitors to act on.',
            'Rebuild the homepage around trust, proof, and one clear next step.',
            'Create dedicated sections for your highest-value services.',
            'Improve the mobile experience and call-to-action visibility.',
            'Add tracking so you know which pages and sources create enquiries.',
        ],
        build_path: 'Conversion Website + Tracking Setup',
    },
    funnel: {
        explanation:
            'Traffic is reaching your offer — but the path from interest to action isn\'t focused enough. People consider, hesitate, and quietly drop off instead of converting.',
        bottleneck:
            'You\'re likely losing clients at this stage: people see the offer but the next step isn\'t focused or persuasive enough to move them to book.',
        next_steps: [
            'Choose one high-value offer or service to promote.',
            'Build a dedicated landing page instead of sending traffic to a generic homepage.',
            'Add a short qualification flow before the booking or contact step.',
            'Track every lead by source, status, and outcome.',
            'Review lead quality before increasing ad spend.',
        ],
        build_path: 'Campaign Funnel + Lead Tracking Dashboard',
    },
    dashboard: {
        explanation:
            'Leads are coming in from multiple places — but without a clear view of who\'s where, opportunities quietly slip out of sight and marketing decisions become guesswork.',
        bottleneck:
            'You\'re likely losing clients at this stage: leads come in but you can\'t see which ones are being dropped, missed, or left without follow-up.',
        next_steps: [
            'Define the stages every lead should move through.',
            'Track lead source, status, owner, and next action.',
            'Separate enquiries from booked consultations or sales.',
            'Review dropped leads weekly.',
            'Connect campaign performance to real business outcomes.',
        ],
        build_path: 'Lead Tracking Dashboard + Follow-Up Workflow',
    },
    ai: {
        explanation:
            'People are reaching out — but not converting into bookings. This is where your system is leaking. Response speed and follow-up consistency are where most of the drop-off happens.',
        bottleneck:
            'You\'re likely losing clients at this stage: enquiries come in, but slow or inconsistent follow-up means they go cold before they book.',
        next_steps: [
            'Map what should happen after every enquiry or missed call.',
            'Create a standard qualification script.',
            'Add automated reminders or follow-up messages.',
            'Summarise every call or enquiry into a clear lead profile.',
            'Track which leads still need action.',
        ],
        build_path: 'AI Follow-Up Workflow + Lead Tracking Dashboard',
    },
    custom: {
        explanation:
            'The drop-off isn\'t only on the marketing side. Information and work are scattered across spreadsheets, inboxes and tools — so leads, tasks, and decisions quietly fall between the cracks.',
        bottleneck:
            'You\'re likely losing clients at this stage: work and information are scattered, so opportunities slip through the cracks before anyone acts on them.',
        next_steps: [
            'Identify the workflow that creates the most friction.',
            'Define the users, actions, and data that need to be managed.',
            'Replace scattered spreadsheets or manual tracking with one clear interface.',
            'Add dashboards for the most important decisions.',
            'Automate repetitive handoffs where possible.',
        ],
        build_path: 'Custom Dashboard / Web App + Automation Logic',
    },
    ecom: {
        explanation:
            'Shoppers are reaching your products — but hesitating or abandoning before completing the order. The buying journey isn\'t guiding them decisively toward the right choice.',
        bottleneck:
            'You\'re likely losing clients at this stage: customers reach the product but hesitate or abandon before completing the order.',
        next_steps: [
            'Identify where customers hesitate before purchase.',
            'Improve product pages around clarity, trust, and decision support.',
            'Add bundles or guided product selection if customers have multiple options.',
            'Make pricing, customisation, and delivery expectations obvious.',
            'Track abandoned carts and follow up where appropriate.',
        ],
        build_path: 'E-Commerce Flow + Bundle/Upsell System',
    },
};

function scoreAnswers(a) {
    const has = (arr, v) => (arr || []).includes(v);
    const scores = { website: 0, funnel: 0, dashboard: 0, ai: 0, custom: 0, ecom: 0 };

    // Conversion Website
    if (has(a.current_setup, 'No website yet')) scores.website += 3;
    if (has(a.current_setup, 'Basic website')) scores.website += 2;
    if (a.main_goal === 'More enquiries') scores.website += 1;
    if (a.biggest_leak === 'People visit but do not enquire') scores.website += 2;
    if (a.biggest_leak === 'The website does not explain the offer well') scores.website += 3;
    if (a.biggest_leak === 'The business looks less premium than it is') scores.website += 2;

    // Campaign Funnel
    if (a.main_goal === 'More enquiries') scores.funnel += 2;
    if (a.main_goal === 'More booked consultations') scores.funnel += 2;
    if (a.main_goal === 'Better lead quality') scores.funnel += 2;
    if (a.main_goal === 'Launch a new offer') scores.funnel += 3;
    if (has(a.current_setup, 'Ads running')) scores.funnel += 2;
    if (has(a.current_setup, 'Landing pages for campaigns')) scores.funnel += 1;
    if (a.biggest_leak === 'People visit but do not enquire') scores.funnel += 1;
    if (a.biggest_leak === 'Leads are low quality') scores.funnel += 2;

    // Lead Tracking Dashboard
    if (a.main_goal === 'Better lead tracking') scores.dashboard += 3;
    if (a.biggest_leak === 'We do not know where leads come from') scores.dashboard += 3;
    if (has(a.current_setup, 'Dashboard / CRM')) scores.dashboard += 1;
    if ((a.lead_sources || []).length >= 3) scores.dashboard += 1;
    if (a.after_enquiry === 'We track status and outcome') scores.dashboard += 1;
    if (a.after_enquiry === 'They go into a CRM') scores.dashboard += 1;
    if (a.after_enquiry === 'Honestly, it is messy') scores.dashboard += 2;

    // AI Receptionist / Follow-Up
    if (a.main_goal === 'Faster follow-up') scores.ai += 3;
    if (a.biggest_leak === 'Leads enquire but do not book') scores.ai += 3;
    if (a.biggest_leak === 'Follow-up is slow or inconsistent') scores.ai += 3;
    if (a.after_enquiry === 'We reply manually when we can') scores.ai += 2;
    if (a.after_enquiry === 'Reception/team calls them') scores.ai += 1;
    if (a.after_enquiry === 'Honestly, it is messy') scores.ai += 2;

    // Custom Web App / Internal System
    if (a.main_goal === 'Improve internal operations') scores.custom += 3;
    if (a.biggest_leak === 'Operations are messy behind the scenes') scores.custom += 3;
    if (has(a.current_setup, 'Dashboard / CRM')) scores.custom += 1;
    if (has(a.current_setup, 'AI or automation tools')) scores.custom += 1;
    if (a.after_enquiry === 'Honestly, it is messy') scores.custom += 1;

    // E-Commerce Sales Flow
    if (a.business_type === 'E-commerce brand') scores.ecom += 4;
    if (a.main_goal === 'More online sales') scores.ecom += 3;
    if (a.biggest_leak === 'People visit but do not enquire') scores.ecom += 1;
    if (has(a.current_setup, 'Basic website')) scores.ecom += 1;
    if (has(a.current_setup, 'Website with booking/contact form')) scores.ecom += 1;

    return scores;
}

function resolveTieBreaker(scores, a) {
    const max = Math.max(...Object.values(scores));
    const tied = Object.keys(scores).filter((k) => scores[k] === max);
    if (tied.length === 1) return tied[0];

    const t = a.business_type;
    const g = a.main_goal;
    const cs = a.current_setup || [];

    if (t === 'E-commerce brand' && tied.includes('ecom')) return 'ecom';
    if (g === 'Improve internal operations' && tied.includes('custom')) return 'custom';
    if (g === 'Better lead tracking' && tied.includes('dashboard')) return 'dashboard';
    if (
        (t === 'Dental clinic' || t === 'Aesthetic / wellness clinic') &&
        g === 'More booked consultations' &&
        tied.includes('funnel')
    ) return 'funnel';
    if (
        (t === 'Dental clinic' || t === 'Aesthetic / wellness clinic') &&
        g === 'Faster follow-up' &&
        tied.includes('ai')
    ) return 'ai';
    if (
        (cs.includes('No website yet') || cs.includes('Basic website')) &&
        tied.includes('website')
    ) return 'website';

    // Fallback order
    const fallback = ['funnel', 'website', 'ai', 'dashboard', 'ecom', 'custom'];
    for (const k of fallback) if (tied.includes(k)) return k;
    return tied[0];
}

function pickSecondary(primaryKey, scores) {
    const others = Object.entries(scores)
        .filter(([k]) => k !== primaryKey)
        .sort((a, b) => b[1] - a[1]);
    if (others.length && others[0][1] > 0) return others[0][0];
    return DEFAULT_SECONDARY[primaryKey];
}

function leadScore(a) {
    const hotBudgets = ['€1,000–€2,500', '€2,500–€5,000', '€5,000+'];
    const hotTimes = ['Immediately', 'This month'];
    const warmTimes = ['In the next 1–3 months'];

    if (
        hotTimes.includes(a.timeline) &&
        hotBudgets.includes(a.budget) &&
        !!a.main_goal
    ) {
        return 'Hot Lead';
    }
    if (a.timeline === 'Just exploring' || a.budget === 'Under €500') {
        if (a.biggest_leak === 'Not sure' && !a.main_goal) return 'Nurture Lead';
        if (a.timeline === 'Just exploring' && a.budget === 'Under €500') return 'Nurture Lead';
    }
    if (
        warmTimes.includes(a.timeline) ||
        a.budget === '€500–€1,000' ||
        (a.current_setup && a.current_setup.length > 0 && a.biggest_leak && a.biggest_leak !== 'Not sure')
    ) {
        return 'Warm Lead';
    }
    if (a.timeline === 'Just exploring' || a.budget === 'Under €500') {
        return 'Nurture Lead';
    }
    return 'Warm Lead';
}

export function computeRecommendation(a) {
    const scores = scoreAnswers(a);
    const primaryKey = resolveTieBreaker(scores, a);
    const secondaryKey = pickSecondary(primaryKey, scores);
    const copy = RESULT_COPY[primaryKey];
    return {
        primary_key: primaryKey,
        secondary_key: secondaryKey,
        primary_system: SYSTEMS[primaryKey],
        secondary_system: SYSTEMS[secondaryKey],
        main_bottleneck: copy.bottleneck,
        explanation: copy.explanation,
        next_steps: copy.next_steps,
        build_path: copy.build_path,
        lead_score: leadScore(a),
        scores,
    };
}
